import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { CONCEPT_ICONS } from '../utils/concept-icons';
import { cn } from '../lib/utils';
import { entityDetailHref } from '../utils/entity-utils';
import { handleEntityDetailActivation } from '../utils/entity-detail-activation';
import { truncateLabel } from '../utils/text';
import { textIncludesQuery } from '@narrativebench/editor-core';
import {
  opensEntityDetailInPlace,
  useOpenEntityDetailOptional,
} from '../contexts/OpenEntityDetailContext';
import { useLocale } from '../contexts/LocaleContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  COLOR_RESOURCE_TAG_CHARACTER_BG,
  COLOR_RESOURCE_TAG_CHARACTER_TEXT,
  COLOR_RESOURCE_TAG_CHARACTER_BORDER,
  COLOR_RESOURCE_TAG_LOCATION_BG,
  COLOR_RESOURCE_TAG_LOCATION_TEXT,
  COLOR_RESOURCE_TAG_LOCATION_BORDER,
  COLOR_RESOURCE_TAG_STORYLINE_BG,
  COLOR_RESOURCE_TAG_STORYLINE_TEXT,
  COLOR_RESOURCE_TAG_STORYLINE_BORDER,
} from '../colors';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

export interface EntityTagsOption {
  id: string;
  name: string;
  documentId?: string;
}

export function mentionableToEntityTagOption(entity: {
  id: string;
  label: string;
  documentId?: string;
}): EntityTagsOption {
  return {
    id: entity.id,
    name: entity.label,
    ...(entity.documentId ? { documentId: entity.documentId } : {}),
  };
}

interface EntityTagBadgeProps {
  entityType: keyof typeof ENTITY_TAG_STYLES;
  id: string;
  name: string;
  documentId?: string;
  onRemove?: () => void;
  className?: string;
  maxLabelLength?: number;
  showEditTooltip?: boolean;
}

export const EntityTagBadge: React.FC<EntityTagBadgeProps> = ({
  entityType,
  id,
  name,
  documentId,
  onRemove,
  className,
  maxLabelLength,
  showEditTooltip = false,
}) => {
  const { t } = useLocale();
  const { projectId } = useParams<{ projectId?: string }>();
  const entityDetail = useOpenEntityDetailOptional();
  const { icon: EntityIcon, iconClassName, style: badgeStyle } = ENTITY_TAG_STYLES[entityType];
  const entity = { type: entityType, id, documentId, label: name };
  const href = entityDetailHref(entity, projectId);
  const openInPlace = Boolean(entityDetail && opensEntityDetailInPlace(entityType) && href);
  const label = maxLabelLength ? truncateLabel(name, maxLabelLength) : name;
  const inner = (
    <>
      <EntityIcon className={cn('shrink-0', iconClassName)} />
      <span className="min-w-0 truncate">{label}</span>
    </>
  );

  const activate = (event: React.MouseEvent): void => {
    handleEntityDetailActivation(event, entity, projectId, entityDetail?.openEntityDetail);
  };

  const editTooltip = showEditTooltip
    ? entityType === 'character'
      ? t('character_tag_click_to_edit')
      : entityType === 'location'
        ? t('location_tag_click_to_edit')
        : undefined
    : undefined;

  const interactive = openInPlace ? (
    <button
      type="button"
      className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 overflow-hidden bg-transparent p-0 text-left"
      style={{ color: 'inherit' }}
      onClick={activate}
      onAuxClick={activate}
    >
      {inner}
    </button>
  ) : href ? (
    <Link
      to={href}
      className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 overflow-hidden no-underline"
      style={{ color: 'inherit' }}
      onClick={(event) => event.stopPropagation()}
      onAuxClick={(event) => event.stopPropagation()}
    >
      {inner}
    </Link>
  ) : (
    <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">{inner}</span>
  );

  const labeledInteractive = editTooltip && (href || openInPlace) ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {interactive}
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start">
          {editTooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    interactive
  );

  return (
    <Badge
      variant="secondary"
      className={cn(
        'flex max-w-full min-w-0 items-center gap-1.5 rounded-full px-3 py-1',
        (href || openInPlace) && 'pr-2',
        className
      )}
      style={badgeStyle}
      title={showEditTooltip ? undefined : name}
    >
      {labeledInteractive}
      {onRemove && (
        <button
          type="button"
          aria-label={t('remove')}
          className="rounded-full p-0.5 hover:bg-black/10"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onRemove();
          }}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </Badge>
  );
};

export const ENTITY_TAG_STYLES = {
  character: {
    icon: CONCEPT_ICONS.character,
    iconClassName: 'h-3 w-3',
    style: {
      backgroundColor: COLOR_RESOURCE_TAG_CHARACTER_BG,
      color: COLOR_RESOURCE_TAG_CHARACTER_TEXT,
      borderColor: COLOR_RESOURCE_TAG_CHARACTER_BORDER,
    },
  },
  location: {
    icon: CONCEPT_ICONS.location,
    iconClassName: 'h-3 w-3',
    style: {
      backgroundColor: COLOR_RESOURCE_TAG_LOCATION_BG,
      color: COLOR_RESOURCE_TAG_LOCATION_TEXT,
      borderColor: COLOR_RESOURCE_TAG_LOCATION_BORDER,
    },
  },
  storyline: {
    icon: CONCEPT_ICONS.storyline,
    iconClassName: 'h-3 w-3',
    style: {
      backgroundColor: COLOR_RESOURCE_TAG_STORYLINE_BG,
      color: COLOR_RESOURCE_TAG_STORYLINE_TEXT,
      borderColor: COLOR_RESOURCE_TAG_STORYLINE_BORDER,
    },
  },
} as const;

interface EntityTagsFieldProps {
  label: string;
  entityType: keyof typeof ENTITY_TAG_STYLES;
  options: EntityTagsOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  emptyHint?: string;
  showEditTooltip?: boolean;
}

export const EntityTagsField: React.FC<EntityTagsFieldProps> = ({
  label,
  entityType,
  options,
  selectedIds,
  onChange,
  disabled = false,
  emptyHint,
  showEditTooltip = false,
}) => {
  const { t } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const optionsById = useMemo(() => new Map(options.map((option) => [option.id, option])), [options]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) {
      return options;
    }
    return options.filter((option) => textIncludesQuery(option.name, search));
  }, [options, search]);

  const toggleOption = (id: string, shouldSelect: boolean): void => {
    if (shouldSelect) {
      onChange([...selectedIds, id]);
    } else {
      onChange(selectedIds.filter((selectedId) => selectedId !== id));
    }
  };

  if (options.length === 0) {
    return (
      <div>
        <label className="mb-1.5 block text-sm font-medium">{label}</label>
        {emptyHint && <p className="text-xs text-muted-foreground">{emptyHint}</p>}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div>
        <label className="mb-1.5 block text-sm font-medium">{label}</label>
        <div className="flex max-w-full min-w-0 flex-wrap items-center gap-2">
          {selectedIds.map((id) => {
            const option = optionsById.get(id);
            if (!option) {
              return null;
            }
            return (
              <EntityTagBadge
                key={id}
                entityType={entityType}
                id={option.id}
                name={option.name}
                documentId={option.documentId}
                onRemove={disabled ? undefined : () => toggleOption(id, false)}
                showEditTooltip={showEditTooltip}
              />
            );
          })}
        {!disabled && (
          <DropdownMenu
            open={isOpen}
            onOpenChange={(open) => {
              setIsOpen(open);
              if (!open) {
                setSearch('');
              }
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground"
              >
                {t('add')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="z-[2100] w-[260px] p-2">
              <Input
                type="text"
                placeholder={t('select_one_or_more_options')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                className="mb-1"
              />
              <div className="max-h-32 overflow-y-auto overflow-x-hidden pr-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border">
                {filteredOptions.length === 0 ? (
                  <div className="px-2 py-3 text-center text-sm text-muted-foreground">{t('no_options_found')}</div>
                ) : (
                  filteredOptions.map((option) => {
                    const checked = selectedIds.includes(option.id);
                    return (
                      <DropdownMenuItem
                        key={option.id}
                        onSelect={(e) => {
                          e.preventDefault();
                          toggleOption(option.id, !checked);
                        }}
                      >
                        <span
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border border-primary',
                            checked && 'bg-primary text-primary-foreground'
                          )}
                          aria-hidden
                        >
                          {checked && <Check className="h-3.5 w-3.5" />}
                        </span>
                        {option.name}
                      </DropdownMenuItem>
                    );
                  })
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
    </TooltipProvider>
  );
};
